<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { Check, Database, ExternalLink, LogOut, Radio, Save, ServerCog, ShieldCheck } from "@lucide/vue";
import { api } from "@/lib/api";

const DEFAULT_ARTIST = "夜栖电波";
const DEFAULT_DISTRIBUTOR = "网易音乐人自主发行";
const storedArtist = localStorage.getItem("wavecraft-artist");
const router = useRouter();
const profile = reactive({
  artist: storedArtist && storedArtist !== "我的厂牌" ? storedArtist : DEFAULT_ARTIST,
  distributor: localStorage.getItem("wavecraft-distributor") || DEFAULT_DISTRIBUTOR,
  contact: localStorage.getItem("wavecraft-contact") || "",
});
const health = ref<{
  model: string;
  configured: boolean;
  authenticated: boolean;
  database: "mysql" | "local";
} | null>(null);
const username = ref("admin");
const message = ref("");

onMounted(async () => {
  localStorage.setItem("wavecraft-artist", profile.artist);
  localStorage.setItem("wavecraft-distributor", profile.distributor);
  try {
    health.value = await api.health();
    username.value = (await api.session()).username || "admin";
  } catch {
    health.value = null;
  }
});

function saveProfile() {
  localStorage.setItem("wavecraft-artist", profile.artist);
  localStorage.setItem("wavecraft-distributor", profile.distributor);
  localStorage.setItem("wavecraft-contact", profile.contact);
  message.value = "工作室资料已保存在当前设备。";
}

async function logout() {
  await api.logout().catch(() => undefined);
  await router.replace("/login");
}
</script>

<template>
  <div class="settings-layout">
    <section class="panel settings-main">
      <div class="section-heading border-bottom"><div><p class="eyebrow">STUDIO PROFILE</p><h3>你的音乐经营身份</h3></div><span class="outline-icon"><ServerCog :size="18" /></span></div>
      <div class="form-section two-column-form">
        <label class="field"><span>艺人名 / 厂牌名</span><input v-model="profile.artist" placeholder="例如：北纬三十度唱片" /></label>
        <label class="field"><span>发行商</span><select v-model="profile.distributor"><option>{{ DEFAULT_DISTRIBUTOR }}</option></select></label>
        <label class="field span-two"><span>版权 / 商务联系邮箱</span><input v-model="profile.contact" type="email" placeholder="用于发行资料与听众联系" /></label>
      </div>
      <div class="identity-access distributor-access">
        <span><Radio :size="17" /></span>
        <div>
          <strong>网易音乐人自主发行通道已启用</strong>
          <p>发行中心会按网易云所需结构整理母带、封面、元数据与 AI 创作说明。</p>
        </div>
        <a class="text-link" href="https://music.163.com/creatorcenter" target="_blank" rel="noreferrer">官方后台 <ExternalLink :size="14" /></a>
      </div>
      <button class="button button-dark" @click="saveProfile"><Save :size="16" />保存工作室资料</button>
      <p v-if="message" class="settings-message">{{ message }}</p>
    </section>

    <section class="panel settings-main">
      <div class="section-heading border-bottom"><div><p class="eyebrow">PRIVATE ACCESS</p><h3>私有服务与登录会话</h3></div><span class="outline-icon"><ShieldCheck :size="18" /></span></div>
      <div class="connection-card">
        <div class="server-status">
          <span :class="{ online: health?.configured }"><Check :size="20" /></span>
          <div><strong>MiniMax 后端代理 {{ health?.configured ? "已连接" : "未连接" }}</strong><p>模型：{{ health?.model || "无法读取" }} · API Key 仅保存在服务器</p></div>
        </div>
        <div class="identity-access">
          <span><ShieldCheck :size="17" /></span>
          <div><strong>当前登录：{{ username }}</strong><p>使用 HttpOnly 加密会话，管理员密码不会保存在前端。</p></div>
          <button class="button button-ghost button-sm" @click="logout"><LogOut :size="14" />退出登录</button>
        </div>
        <div class="identity-access">
          <span><Database :size="17" /></span>
          <div><strong>作品数据：{{ health?.database === "mysql" ? "MySQL 持久化" : "本地开发存储" }}</strong><p>作品元数据与母带音频分开持久保存，容器更新不会清空数据。</p></div>
        </div>
      </div>
    </section>

    <aside class="panel security-note">
      <ShieldCheck :size="24" />
      <h3>私有部署安全边界</h3>
      <p>浏览器只通过同源 HTTPS 会话访问服务。MiniMax API Key、管理员密码和数据库口令均由服务端环境变量管理，不会进入前端构建产物或 Git 仓库。</p>
      <ul class="plain-list"><li>API Key 不进入前端包</li><li>登录失败频率限制</li><li>MySQL 与音频独立持久卷</li><li>HTTPS 证书自动续期</li></ul>
    </aside>
  </div>
</template>
